import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import {
  FaFileInvoice,
  FaDownload,
  FaEye,
  FaSync,
  FaFilter,
  FaSearch
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import './UserBilling.css';
import { formatShortDate } from '../../utils/date';

const UserBilling = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { data: billingsData, isLoading, error, refetch } = useQuery(
    ['userBillings', currentPage, itemsPerPage, statusFilter],
    () => {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      if (statusFilter) params.append('status', statusFilter);

      return api.get(`/api/ecommerce/billings?${params.toString()}`);
    },
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: false,
    }
  );

  const billings = billingsData?.data?.data?.billings || [];
  const pagination = billingsData?.data?.data?.pagination || {};

  const handleSyncOrder = async (orderId) => {
    try {
      await api.post(`/api/ecommerce/orders/${orderId}/sync-billing`);
      refetch();
      alert('Order synced to billing successfully!');
    } catch (error) {
      console.error('Error syncing order:', error);
      alert('Error syncing order to billing');
    }
  };

  const handleViewBilling = (billing) => {
    setSelectedBilling(billing);
    setShowModal(true);
  };

  const handleDownloadInvoice = (billing) => {
    if (!billing) return;
    // A4 portrait to match the sample photo
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    // Helpers
    const left = 10;
    const right = doc.internal.pageSize.getWidth() - 12; // a bit more right padding to avoid clipping
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const col = (x) => left + x; // convenience column calculator
    const formatMoney = (n) => Number(n || 0).toFixed(2);
    const padPcs = (n) => `${Number(n || 0).toFixed(2)}Pcs`;
    const formatDateDDMMYYYY = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const yyyy = dt.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    };
    const numberToIndianWords = (amount) => {
      // Supports up to Crores comfortably; tailored to Indian system used in the sample
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const two = (num) => {
        if (num < 20) return ones[num];
        const t = Math.floor(num / 10);
        const o = num % 10;
        return `${tens[t]}${o ? ' ' + ones[o] : ''}`.trim();
      };
      const three = (num) => {
        const h = Math.floor(num / 100);
        const r = num % 100;
        const hPart = h ? `${ones[h]} Hundred${r ? ' ' : ''}` : '';
        return `${hPart}${two(r)}`.trim();
      };
      const n = Math.round(Number(amount || 0));
      if (n === 0) return 'Zero';
      const crore = Math.floor(n / 10000000);
      const lakh = Math.floor((n % 10000000) / 100000);
      const thousand = Math.floor((n % 100000) / 1000);
      const hundred = Math.floor((n % 1000));
      const parts = [];
      if (crore) parts.push(`${two(crore)} Crore`);
      if (lakh) parts.push(`${two(lakh)} Lakh`);
      if (thousand) parts.push(`${two(thousand)} Thousand`);
      if (hundred) parts.push(three(hundred));
      return parts.join(' ').replace(/\s+/g, ' ').trim();
    };

    // Compute figures
    const subtotal = Number(billing.subtotal || 0);
    const discount = Number(billing.totalDiscount || 0);
    const total = Number(billing.totalAmount || (subtotal - discount));
    const deliveryCharge = Number(billing.deliveryCharge || 0);
    const previousBalance = Number(billing.previousBalance || 0);
    const gross = subtotal - discount + deliveryCharge + previousBalance;
    const roundedBillValue = Math.round(gross);
    const roundOff = (roundedBillValue - gross).toFixed(2);

    // Title: ESTIMATE
    let y = 10;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('ESTIMATE', pageWidth / 2, y, { align: 'center' });

    // Header left: To, customer
    y += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('To,', left, y);
    y += 6;
    const toLines = [
      billing.customerName || '',
      [
        billing.customerAddress?.street,
        billing.customerAddress?.city
      ].filter(Boolean).join(', '),
      [
        billing.customerAddress?.state,
        billing.customerAddress?.zipCode
      ].filter(Boolean).join(', ')
    ].filter(Boolean);
    toLines.forEach((line) => { doc.text(String(line), left, y); y += 5; });

    // Header right box: Bill No, Dt, Transpoter
    const boxTop = 18;
    const boxLeft = right - 70;
    const boxWidth = 70;
    const boxHeight = 20;
    doc.setLineWidth(0.2);
    doc.rect(boxLeft, boxTop, boxWidth, boxHeight);
    doc.setFont(undefined, 'bold');
    doc.text('Bill No:', boxLeft + 2, boxTop + 6);
    doc.text('Dt:', boxLeft + 2, boxTop + 12);
    doc.text('Transpoter:', boxLeft + 2, boxTop + 18);
    doc.setFont(undefined, 'normal');
    doc.text(String(billing.billingNo || ''), boxLeft + 30, boxTop + 6);
    doc.text(formatDateDDMMYYYY(billing.createdAt || billing.date), boxLeft + 30, boxTop + 12);
    doc.text(String(billing.transporter || billing.transport || ''), boxLeft + 30, boxTop + 18);

    // Table header (with dotted verticals like the sample)
    y = 48;
    const tableLeft = left;
    const tableRight = right;
    // Column widths must sum to available width (right - left)
    // Available = pageWidth - 22 (~190mm with our margins)
    const colWidths = {
      sl: 8,
      product: 82,
      ctn: 14,
      qty: 20,
      rate: 26,
      disc: 16,
      amount: 24
    };
    const colX = {
      sl: tableLeft,
      product: tableLeft + colWidths.sl,
      ctn: tableLeft + colWidths.sl + colWidths.product,
      qty: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn,
      rate: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn + colWidths.qty,
      disc: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn + colWidths.qty + colWidths.rate,
      amount: tableLeft + colWidths.sl + colWidths.product + colWidths.ctn + colWidths.qty + colWidths.rate + colWidths.disc
    };
    const drawTableHeader = (yStart) => {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Sl.', colX.sl + 1, yStart);
      doc.text('Product Name', colX.product + 1, yStart);
      doc.text('Ctn', colX.ctn + 1, yStart);
      doc.text('Qty', colX.qty + colWidths.qty - 2, yStart, { align: 'right' });
      doc.text('Rate', colX.rate + colWidths.rate - 2, yStart, { align: 'right' });
      doc.text('Disc%', colX.disc + colWidths.disc - 2, yStart, { align: 'right' });
      doc.text('Amount', colX.amount + colWidths.amount - 2, yStart, { align: 'right' });
      // Top dotted divider under header
      doc.setLineDash([1, 1], 0);
      doc.line(tableLeft, yStart + 2, tableRight, yStart + 2);
      doc.setLineDash();
      // Column dotted separators down to footer separator height
      const colBottom = pageHeight - 70;
      const drawCol = (x) => {
        doc.setLineDash([1, 1], 0);
        doc.line(x, yStart - 6, x, colBottom);
        doc.setLineDash();
      };
      drawCol(colX.product);
      drawCol(colX.ctn);
      drawCol(colX.qty);
      drawCol(colX.rate);
      drawCol(colX.disc);
      drawCol(colX.amount);
    };
    drawTableHeader(y);

    // Items rows
    let iy = y + 8;
    doc.setFontSize(8.6); // slightly smaller to ensure numeric columns fit
    doc.setFont(undefined, 'normal');
    const items = Array.isArray(billing.items) ? billing.items : [];
    const maxRows = 20; // exactly 20 items per page
    let rowIndex = 0;

    const renderRow = (idx, item) => {
      const name = item.inventory?.itemName || item.itemName || '';
      const qty = item.quantity || 0;
      const rate = Number(item.sellingPrice || 0);
      const disc = Number(item.discount || 0);
      const amount = (rate - rate * (disc / 100)) * qty;
      // Clip long product names to available space so numeric columns never shift
      const maxNameChars = 48;
      const clippedName = String(name).length > maxNameChars ? String(name).slice(0, maxNameChars - 1) + '…' : String(name);
      doc.text(String(idx + 1), colX.sl + 1, iy);
      doc.text(clippedName, colX.product + 1, iy, { maxWidth: colWidths.product - 2 });
      // Ctn not tracked; leave blank to mimic sample layout
      doc.text(padPcs(qty), colX.qty + colWidths.qty - 2, iy, { align: 'right' });
      // Use monospaced digits for numeric columns for tighter/consistent width
      doc.setFont(undefined, 'normal');
      doc.text(formatMoney(rate), colX.rate + colWidths.rate - 0.5, iy, { align: 'right' });
      doc.text(String(disc ? disc.toFixed(0) : ''), colX.disc + colWidths.disc - 2, iy, { align: 'right' });
      doc.text(formatMoney(amount), colX.amount + colWidths.amount - 0.5, iy, { align: 'right' });
    };

    const addFooterAndMaybeNewPage = (isLastPage) => {
      // Bottom dashed separator like in sample
      const sepY = pageHeight - 70;
      doc.setLineDash([1.5, 1.5], 0);
      doc.line(tableLeft, sepY, tableRight, sepY);
      doc.setLineDash();

      // Footer values block on the right
      let fy = sepY + 8;
      const label = (t) => { doc.text(t, right - 70, fy); };
      const value = (v) => { doc.text(String(v), right, fy, { align: 'right' }); };
      doc.setFont(undefined, 'normal');
      label('%:'); value(''); fy += 8; // matches photo having a standalone % label
      label('Add :Delivery Charges'); value(formatMoney(deliveryCharge)); fy += 8;
      const balDate = billing.previousBalanceDate || billing.balanceDate || billing.createdAt;
      label(`Add :Balence   ${formatDateDDMMYYYY(balDate)}`); value(formatMoney(previousBalance)); fy += 10;
      label('Round Off'); value(formatMoney(roundOff)); fy += 10;
      doc.setFont(undefined, 'bold');
      label('Bill Value'); value(formatMoney(roundedBillValue));

      // Amount in words (Indian system) at the very bottom-left
      const words = `${numberToIndianWords(roundedBillValue)} Only.`;
      doc.setFont(undefined, 'normal');
      doc.text(words, tableLeft, pageHeight - 15);
    };

    for (let i = 0; i < items.length; i++) {
      renderRow(i, items[i]);
      rowIndex++;
      iy += 6; // row height similar to sample
      if (rowIndex % maxRows === 0 && i < items.length - 1) {
        // new page: redraw table header and guides only (footer only on last page)
        doc.addPage();
        y = 20;
        drawTableHeader(y);
        iy = y + 8;
      }
    }

    // Totals area (dedicated block on the right, visually separated from table rows)
    const totalsTopY = Math.min(iy + 6, pageHeight - 90);
    const totalsBlockLeft = right - 80;
    const totalsBlockTop = totalsTopY - 4;
    const totalsBlockHeight = (pageHeight - 70) - totalsBlockTop - 16; // allow space for footer labels
    // Clear dotted column guides underneath totals using white fill
    doc.setFillColor(255, 255, 255);
    doc.rect(totalsBlockLeft, totalsBlockTop, 82, Math.max(18, totalsBlockHeight), 'F');
    // Separator above totals block
    doc.setLineDash([1.5, 1.5], 0);
    doc.line(left, totalsTopY - 2, right, totalsTopY - 2);
    doc.setLineDash();
    // Render totals inside the cleared block
    doc.setFont(undefined, 'normal');
    let ty = totalsTopY;
    const writeTotal = (label, val, bold = false) => {
      doc.setFont(undefined, bold ? 'bold' : 'normal');
      doc.text(label, right - 70, ty);
      doc.text(String(val), right, ty, { align: 'right' });
      ty += 6;
    };
    writeTotal('Subtotal', formatMoney(subtotal));
    writeTotal('Discount', formatMoney(discount));
    writeTotal('Total', formatMoney(total), true);

    // Footer (delivery, balance, round off, bill value, words)
    addFooterAndMaybeNewPage(true);

    doc.save(`Bill-${billing.billingNo || 'billing'}.pdf`);
  };

  const filteredBillings = billings.filter(billing =>
    (billing.billingNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (billing.company?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (billing.orderNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid': return 'status-paid';
      case 'pending': return 'status-pending';
      case 'overdue': return 'status-overdue';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-default';
    }
  };

  const getPaymentStatusBadgeClass = (paymentStatus) => {
    switch (paymentStatus) {
      case 'full': return 'payment-full';
      case 'partial': return 'payment-partial';
      case 'due': return 'payment-due';
      default: return 'payment-default';
    }
  };

  if (isLoading) return <Loading />;
  if (error) return <div className="error-message">Error loading billing records</div>;

  return (
    <div className="user-billing-container">
      <div className="billing-header">
        <h1>My Billing Records</h1>
        <p>View and manage your billing records from e-commerce orders</p>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-filter-row">
          <div className="search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by billing number, company, or order number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-controls">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <button onClick={refetch} className="refresh-btn">
              <FaSync /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Billing Records Table */}
      <div className="billing-table-container">
        {filteredBillings.length === 0 ? (
          <div className="no-data-message">
            <FaFileInvoice className="no-data-icon" />
            <h3>No billing records found</h3>
            <p>Your billing records from e-commerce orders will appear here</p>
          </div>
        ) : (
          <>
            <div className="table-header">
              <div className="results-info">
                Showing {filteredBillings.length} of {pagination.totalBillings} billing records
              </div>
              <div className="pagination-controls">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="page-size-select"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>

            <table className="billing-table">
              <thead>
                <tr>
                  <th>Billing No</th>
                  <th>Order No</th>
                  <th>Company</th>
                  <th>Total Amount</th>
                  <th>Amount Paid</th>
                  <th>Due Amount</th>
                  <th>Status</th>
                  <th>Payment Status</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBillings.map((billing) => (
                  <tr key={billing._id}>
                    <td>
                      <div className="billing-number-cell">
                        <FaFileInvoice className="billing-icon" />
                        <span>{billing.billingNo}</span>
                      </div>
                    </td>
                    <td>
                      {billing.orderNumber ? (
                        <span className="order-number">{billing.orderNumber}</span>
                      ) : (
                        <span className="no-order">N/A</span>
                      )}
                    </td>
                    <td>{billing.company?.name || 'N/A'}</td>
                    <td className="amount-cell">₹{billing.totalAmount?.toFixed(2)}</td>
                    <td className="amount-cell">₹{Number(billing.amountPaid || 0).toFixed(2)}</td>
                    <td className="amount-cell">
                      ₹{(Math.max(0, (billing.totalAmount || 0) - (billing.amountPaid || 0))).toFixed(2)}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(billing.status)}`}>
                        {billing.status}
                      </span>
                    </td>
                    <td>
                      <span className={`payment-badge ${getPaymentStatusBadgeClass(billing.paymentStatus)}`}>
                        {billing.paymentStatus}
                      </span>
                    </td>
                    <td>
                      {formatShortDate(billing.dueDate)}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleViewBilling(billing)}
                          className="action-btn view"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(billing)}
                          className="action-btn download"
                          title="Download Invoice"
                        >
                          <FaDownload />
                        </button>
                        {!billing.orderNumber && (
                          <button
                            onClick={() => handleSyncOrder(billing.orderReference)}
                            className="action-btn sync"
                            title="Sync to Order"
                          >
                            <FaSync />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>

                <span className="pagination-info">
                  Page {currentPage} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Billing Details Modal */}
      {showModal && selectedBilling && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content billing-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Billing Details - {selectedBilling.billingNo}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <div className="billing-details-content">
              <div className="billing-info-grid">
                <div className="info-section">
                  <h3>Billing Information</h3>
                  <div className="info-item">
                    <label>Billing Number:</label>
                    <span>{selectedBilling.billingNo}</span>
                  </div>
                  <div className="info-item">
                    <label>Order Number:</label>
                    <span>{selectedBilling.orderNumber || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Company:</label>
                    <span>{selectedBilling.company?.name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Customer Name:</label>
                    <span>{selectedBilling.customerName || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <span>{selectedBilling.customerPhone || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span className={`status-badge ${getStatusBadgeClass(selectedBilling.status)}`}>
                      {selectedBilling.status}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Payment Method:</label>
                    <span>{selectedBilling.paymentMethod || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Due Date:</label>
                    <span>{formatShortDate(selectedBilling.dueDate)}</span>
                  </div>
                  <div className="info-item">
                    <label>Created Date:</label>
                    <span>{formatShortDate(selectedBilling.createdAt)}</span>
                  </div>
                </div>

                <div className="info-section">
                  <h3>Financial Summary</h3>
                  <div className="info-item">
                    <label>Subtotal:</label>
                    <span>₹{selectedBilling.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="info-item">
                    <label>Total Discount:</label>
                    <span>₹{selectedBilling.totalDiscount?.toFixed(2)}</span>
                  </div>
                  <div className="info-item total-amount">
                    <label>Total Amount:</label>
                    <span>₹{selectedBilling.totalAmount?.toFixed(2)}</span>
                  </div>
                  <div className="info-item">
                    <label>Amount Paid:</label>
                    <span>₹{Number(selectedBilling.amountPaid || 0).toFixed(2)}</span>
                  </div>
                  <div className="info-item">
                    <label>Due Amount:</label>
                    <span>₹{(Math.max(0, (selectedBilling.totalAmount || 0) - (selectedBilling.amountPaid || 0))).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="items-section">
                <h3>Billing Items</h3>
                <div className="items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Discount</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBilling.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.inventory?.itemCode || item.itemCode}</td>
                          <td>{item.inventory?.itemName || item.itemName}</td>
                          <td>{item.quantity}</td>
                          <td>₹{item.sellingPrice.toFixed(2)}</td>
                          <td>{item.discount}%</td>
                          <td>₹{item.totalAmount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedBilling.notes && (
                <div className="notes-section">
                  <h3>Notes</h3>
                  <p>{selectedBilling.notes}</p>
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={() => handleDownloadInvoice(selectedBilling)}
                  className="btn-primary"
                >
                  <FaDownload /> Download Invoice
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBilling;
